import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, Phone, X } from "lucide-react";
import { PHONE_DISPLAY, PHONE_TEL, scrollToId, useLang } from "../i18n";
import LangToggle from "./LangToggle";

const NAV = [
  { id: "home", key: "home" },
  { id: "about", key: "about" },
  { id: "services", key: "services" },
  { id: "fleet", key: "fleet" },
  { id: "pricing", key: "pricing" },
  { id: "contact", key: "contact" },
];

export default function Header() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goTo = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 450);
    } else {
      scrollToId(id);
    }
  };

  return (
    <>
    <header className="fixed inset-x-0 top-0 z-50 border-b border-white/5 bg-[#0b0b0b]/95 backdrop-blur">
      <div className="relative mx-auto flex h-[76px] max-w-[1400px] items-center justify-between px-5 lg:px-10">
        <button
          data-testid="header-logo-link"
          onClick={() => goTo("home")}
          aria-label="Singh Taxi — Home"
          className="flex items-center transition-transform hover:scale-[1.03]"
        >
          <img
            src="/assets/singh-taxi-logo.png"
            alt="Singh Taxi"
            className="h-11 w-auto rounded-md"
          />
        </button>

        <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-8 lg:flex">
          {NAV.map((n) => (
            <button
              key={n.id}
              data-testid={`nav-link-${n.id}`}
              onClick={() => goTo(n.id)}
              className="text-[13px] font-semibold text-white/90 transition-colors hover:text-gold"
            >
              {t.nav[n.key]}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-book-button"
            onClick={() => goTo("book")}
            className="hidden items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-[13px] font-extrabold text-black transition-all hover:-translate-y-0.5 hover:bg-gold-light lg:inline-flex"
          >
            <Phone className="h-4 w-4" />
            {t.nav.book}
          </button>
          <button
            data-testid="mobile-menu-open-button"
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 text-white lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>
      </div>
    </header>

    <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] flex flex-col bg-[#0b0b0b] lg:hidden"
          >
            <div className="flex h-[76px] items-center justify-between border-b border-white/5 px-5">
              <button
                data-testid="mobile-menu-logo-link"
                onClick={() => setOpen(false)}
                aria-label="Singh Taxi — Home"
                className="flex items-center"
              >
                <img
                  src="/assets/singh-taxi-logo.png"
                  alt="Singh Taxi"
                  className="h-11 w-auto rounded-md"
                />
              </button>
              <button
                data-testid="mobile-menu-close-button"
                aria-label="Close menu"
                onClick={() => setOpen(false)}
                className="flex h-11 w-11 items-center justify-center rounded-xl border border-white/15 text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <nav className="flex-1 space-y-6 overflow-y-auto px-8 pt-10">
              {NAV.map((n, i) => (
                <button
                  key={n.id}
                  data-testid={`mobile-nav-link-${n.id}`}
                  onClick={() => goTo(n.id)}
                  className="group flex items-baseline gap-4 text-left"
                >
                  <span className="text-xs font-bold text-gold">
                    0{i + 1}
                  </span>
                  <span className="font-serif text-4xl text-white transition-colors group-hover:text-gold">
                    {t.nav[n.key]}
                  </span>
                </button>
              ))}
            </nav>

            <div className="space-y-5 px-8 pb-10">
              <button
                data-testid="mobile-book-button"
                onClick={() => goTo("book")}
                className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-4 text-base font-extrabold text-black"
              >
                <Phone className="h-5 w-5" />
                {t.nav.book}
              </button>
              <div className="flex items-center justify-between">
                <LangToggle />
                <a
                  data-testid="mobile-phone-link"
                  href={PHONE_TEL}
                  className="text-base font-bold text-white"
                >
                  {PHONE_DISPLAY}
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
